<template>
  <div class="flex flex-col">
    <!-- Which way the game leans, in the two teams' own colours, with the
         frozen and market numbers marked on it. The lit band between the two
         marks IS the edge -- the span of margins our number wins and the
         market's does not.

         Read the bar as a tug of war: the tick is a BOUNDARY, not a team,
         so it slides into the loser's territory. A home team getting more
         favoured drags the tick LEFT even though home is drawn on the right.
         That is why `marketIsLeft` is named for the tick and not for a team,
         and why the arrow below is direction-of-travel across the bar rather
         than a pointer at the side it names.

         The two captions swap so they sit in the same order as their ticks,
         and the arrow between them runs frozen -> market. Their order is not
         fixed, so each one carries its own label and a coloured dot matching
         its tick. Both are written team-first ("CIN -2.5"), which is also how
         a favourite-flip becomes visible -- the abbreviation itself changes. -->
    <div v-if="edge.frozen != null">
      <!-- On the Home card the bar's box is taller (the numbers hang under
           it), so the team labels pin to the bar itself rather than centring
           on the whole box. -->
      <div class="flex gap-2" :class="card ? 'items-start' : 'items-center'">
        <span
          class="w-7 shrink-0 text-[11px] font-bold tracking-wider"
          :class="card ? '-mt-[3px]' : ''"
          :style="{ color: awayAccent }"
        >
          {{ awayAbbr }}
        </span>

        <div class="relative flex-1" :class="card ? 'pb-[34px]' : ''">
          <div class="flex h-2 overflow-hidden rounded-full">
            <div :style="{ width: splitShare + '%', background: awayFill }"></div>
            <div class="flex-1" :style="{ background: homeFill }"></div>
          </div>

          <!-- Key numbers as a ruler UNDER the bar, not lines through it:
               through-lines read as a third kind of tick next to frozen and
               market. Labelled with the bare margin, since the captions carry
               the team. -->
          <!-- Tap (phone) or hover (desktop) explains the mark. The button is
               wider than the 1px notch so a thumb can actually hit it. -->
          <button
            v-for="mark in keyNumberMarks"
            :key="mark.spread"
            type="button"
            class="absolute top-2 z-[2] flex w-5 -translate-x-1/2 flex-col items-center pt-0.5"
            :style="{ left: mark.share + '%' }"
            :aria-label="mark.tip"
            @mouseenter="activeKeyNumber = mark.spread"
            @mouseleave="activeKeyNumber = null"
            @click.stop="activeKeyNumber = activeKeyNumber === mark.spread ? null : mark.spread"
          >
            <span class="h-1 w-px bg-amber-300/70"></span>
            <span class="text-[8px] font-semibold leading-none text-amber-300/80">{{ mark.label }}</span>
            <span
              v-if="activeKeyNumber === mark.spread"
              class="pointer-events-none absolute top-full mt-1 w-44 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-left text-[10px] font-normal leading-snug text-slate-200 shadow-lg shadow-black/50"
              :class="mark.share < 30 ? 'left-0' : mark.share > 70 ? 'right-0' : 'left-1/2 -translate-x-1/2'"
            >
              {{ mark.tip }}
            </span>
          </button>

          <!-- The band is the stretch the line moved, in the colour of the
               side it moved toward: that team's colour has grown from the
               white frozen mark to where the books sit now. -->
          <div
            v-if="showBand"
            class="absolute top-0 h-2"
            :style="{ left: bandLeft + '%', width: bandWidth + '%', background: edgeSideAccent, boxShadow: `0 0 10px ${edgeSideAccent}e6` }"
          ></div>

          <span
            class="absolute -top-1 h-4 w-0.5 -translate-x-1/2 rounded-full bg-white shadow shadow-black/60"
            :style="{ left: frozenShare + '%' }"
            aria-hidden="true"
          ></span>
          <span
            v-if="edge.market != null"
            class="absolute -top-1 h-4 w-[3px] -translate-x-1/2 rounded-full ring-1 ring-white/80"
            :style="{ left: marketShare + '%', background: edgeSideAccent, boxShadow: `0 0 6px ${edgeSideAccent}e6` }"
            aria-hidden="true"
          ></span>

          <!-- Home card: each number hangs under its own mark, below the key
               numbers, and grows away from the other one -- the left mark's
               number ends at its mark, the right one's starts at it -- so two
               marks a point apart never collide. The team in the middle of the
               old caption line is gone: the logo in the box below says it. -->
          <template v-if="card">
            <span class="absolute top-[26px] whitespace-nowrap text-[12px]" :style="leftMarkLabelStyle">
              <span class="text-[9px] font-semibold uppercase tracking-[0.12em]" :class="leftCaption.labelClass" :style="leftCaption.labelStyle">{{ leftCaption.label }}</span>
              <span class="ml-1 font-extrabold tabular-nums" :class="leftCaption.valueClass" :style="leftCaption.valueStyle">{{ leftCaption.value }}</span>
            </span>
            <span v-if="edge.market != null" class="absolute top-[26px] whitespace-nowrap text-[12px]" :style="rightMarkLabelStyle">
              <span class="text-[9px] font-semibold uppercase tracking-[0.12em]" :class="rightCaption.labelClass" :style="rightCaption.labelStyle">{{ rightCaption.label }}</span>
              <span class="ml-1 font-extrabold tabular-nums" :class="rightCaption.valueClass" :style="rightCaption.valueStyle">{{ rightCaption.value }}</span>
            </span>
          </template>
        </div>

        <span
          class="w-7 shrink-0 text-right text-[11px] font-bold tracking-wider"
          :class="card ? '-mt-[3px]' : ''"
          :style="{ color: homeAccent }"
        >
          {{ homeAbbr }}
        </span>
      </div>
    </div>

    <!-- One line: frozen, the side the move favours, market. The market
         caption wears the edge side's colour, the same as its mark and the
         band, so the number and the picture agree. The game page only; the
         Home card hangs the two numbers off their marks instead. -->
    <div
      v-if="edge.frozen != null && !card"
      class="mt-4 flex items-center justify-center gap-2.5 whitespace-nowrap text-[15px] sm:gap-3"
    >
        <span class="inline-flex items-baseline gap-1.5">
          <span class="h-[5px] w-[5px] shrink-0 self-center rounded-full" :class="leftCaption.dotClass" :style="leftCaption.dotStyle" aria-hidden="true"></span>
          <span class="text-[9px] font-semibold uppercase tracking-[0.14em]" :class="leftCaption.labelClass" :style="leftCaption.labelStyle">{{ leftCaption.label }}</span>
          <span class="font-extrabold tabular-nums" :class="leftCaption.valueClass" :style="leftCaption.valueStyle">{{ leftCaption.value }}</span>
        </span>

        <span
          v-if="edge.side"
          class="flex h-5 shrink-0 items-center gap-0.5 rounded-full px-1.5 text-[10px] font-extrabold"
          :style="{ color: edgeSideAccent, background: edgeSideAccent + '2e', filter: 'brightness(1.35)' }"
          :aria-label="`Market moved toward ${edgeSideAbbr}`"
        >
          <svg v-if="marketIsLeft" class="h-2.5 w-2.5" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6.75 4.5 12l6 5.25M19 12H5" />
          </svg>
          <span>{{ edgeSideAbbr }}</span>
          <svg v-if="!marketIsLeft" class="h-2.5 w-2.5" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6.75 19.5 12l-6 5.25M5 12h14" />
          </svg>
        </span>
        <svg v-else class="h-3.5 w-3.5 shrink-0 text-slate-600" fill="none" stroke="currentColor" stroke-width="2.25" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6.75 19.5 12l-6 5.25M5 12h14" />
        </svg>

        <span class="inline-flex items-baseline gap-1.5">
          <span class="text-[9px] font-semibold uppercase tracking-[0.14em]" :class="rightCaption.labelClass" :style="rightCaption.labelStyle">{{ rightCaption.label }}</span>
          <span class="font-extrabold tabular-nums" :class="rightCaption.valueClass" :style="rightCaption.valueStyle">{{ rightCaption.value }}</span>
          <span class="h-[5px] w-[5px] shrink-0 self-center rounded-full" :class="rightCaption.dotClass" :style="rightCaption.dotStyle" aria-hidden="true"></span>
        </span>
    </div>

    <!-- The verdict. Ranked and coloured on probability, because that is what
         the ranking is: expected pool points. The point gap and the key
         number it crosses are how you *describe* that number to a human, so
         they sit on the evidence line underneath rather than in the headline
         -- which is the wrong way round in every earlier version of this card. -->
    <div
      :class="card
        ? ['rounded-xl px-3 py-2.5', edge.frozen != null ? 'mt-2' : '']
        : ['rounded-xl px-3 py-3 ring-1 sm:px-4 sm:py-3.5', verdictSurfaceClass, edge.frozen != null ? 'mt-3.5 sm:mt-4' : '']"
      :style="card ? cardVerdictStyle : null"
    >
      <template v-if="edge.measurable">
        <!-- Wraps rather than squeezes: in the narrower card opened inside
             Your Five the strength word and meter drop under the number
             instead of "to win" breaking or running into them. -->
        <div class="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <div class="flex min-w-0 items-baseline gap-1.5">
            <!-- Whose win this is, as the team's own logo: the colour of the
                 number is already spoken for by the strength band. -->
            <img
              v-if="card && edgeSideLogo"
              :src="edgeSideLogo"
              :alt="edgeSideAbbr"
              class="mr-0.5 h-[26px] w-[26px] shrink-0 self-center object-contain"
            />
            <span class="text-[27px] font-extrabold leading-none tracking-tight tabular-nums sm:text-3xl" :class="edgeTextClass" :style="{ textShadow: edgeGlow }">{{ coverLabel }}</span>
            <span class="whitespace-nowrap text-[11px] text-slate-400 sm:text-xs">to win</span>
          </div>

          <div class="shrink-0" :class="card ? 'flex items-center gap-2' : ''">
            <!-- The word bands LINE VALUE, while the big number beside it is
                 the win probability. Those are different quantities and the
                 label has to say which one it is describing: "50.4% Slim" sat
                 next to "51.7% Thin" and read as a contradiction, when in fact
                 the first holds more stale-line value and the second's extra
                 win probability is the books' price lean, not our number. -->
            <p
              class="text-right text-[10px] font-extrabold uppercase tracking-[0.14em] sm:text-[11px]"
              :class="[edgeTextClass, card ? '' : 'mb-1.5']"
            >
              {{ strengthLabel }}
            </p>
            <div class="flex h-2 justify-end gap-[2px] sm:h-2.5" :aria-label="meterLabel" role="img">
              <span
                v-for="i in 10"
                :key="i"
                class="w-[5px] rounded-[1px]"
                :class="i <= meterFilled ? meterFillClass : 'bg-white/10'"
              ></span>
            </div>
          </div>
        </div>

        <!-- One line at every width, nothing hidden on a phone: value, the key
             number crossed (only when our number really wins it), how the books
             lean, and the tie chance. The point gap is in the captions above
             the bar; the posted-movement count and σ are in the Books panel.
             Equal columns, each item centred in its own: justify-between flung
             a desktop card's items to the edges, and a centred "·" run bunched
             them in the middle. `flex-1 basis-0` makes the columns equal and
             `min-w-max` lets a long item ("10/10 books lean WSH") take what it
             needs on a phone rather than wrap or overflow. Without the
             separators the worst case ("+6.2% value  crosses 10  10/10 books
             lean WSH  6.1% tie") fits the 270px a 360px phone gives it at 10px. -->
        <p
          v-if="detail"
          class="mt-2 flex items-center whitespace-nowrap border-t border-white/10 pt-1.5 text-[10px] tracking-tight tabular-nums text-slate-400 sm:text-[11px] sm:tracking-normal"
        >
          <span
            v-for="item in lineItems"
            :key="item.key"
            class="min-w-max flex-1 basis-0 text-center"
            :class="item.key === 'value' ? valueClass : ''"
          >{{ item.text }}</span>
        </p>
      </template>

      <template v-else-if="edge.frozen == null">
        <div class="flex items-end justify-between gap-3">
          <div>
            <p class="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Frozen
            </p>
            <p
              class="mt-1 text-sm font-semibold text-slate-400"
              :title="'The league\'s spread arrives in ' + (game.lineEmailExpected || 'Friday') + '\'s email'"
            >
              {{ dueLabel }}
            </p>
          </div>
          <div class="text-right">
            <p class="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Market
            </p>
            <p class="mt-1 text-sm font-semibold tabular-nums text-slate-300">
              {{ marketLine }}
            </p>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="flex items-center justify-between gap-3">
          <span class="text-sm font-semibold text-slate-400">Edge —</span>
          <span class="text-xs text-slate-400">Waiting for market line</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  computeEdge,
  edgeBand as bandFor,
  formatLine,
  marginPmf,
} from "../utils/edge.js";
import { BAND_GLOW, BAND_TEXT_CLASS, bandLabel, verdictLine, winLabel } from "../utils/verdictLine.js";
import { teamAccent } from "../utils/teamColors.js";
import { teamLogo } from "../utils/teamLogo.js";

// Pixels between each of the Home card's hanging numbers and its own mark, so
// the two are always at least twice this apart however close the marks are.
const MARK_LABEL_GAP = 8;

// The bar is a zoomed window, not the whole spread range: it is centred on the
// midpoint of the frozen and market numbers and spans this many points either
// side. Its job is to make the gap between the two legible -- every real edge
// is 0-4 points -- and the captions already say who is favoured and by how
// much. A fixed ±14 range drew a one-point move at ~10px on a phone, and past
// the clamp both ticks pinned to the end and the gap vanished outright.
const TUG_HALF_WINDOW_POINTS = 6;
const TUG_PERCENT_PER_POINT = 50 / TUG_HALF_WINDOW_POINTS;
// Margins football lands on most often, drawn faintly inside the window so a
// band that crosses one is visibly crossing it. 0 is not a key margin; it is
// drawn so a favourite-flip is visible as the band crossing pick'em.
const TUG_KEY_NUMBERS = [3, 7, 10, 14];

// One segment per percentage point of probability gained, capped. Past ten
// points the meter is pinned and the number beside it does the talking.
const METER_MAX_GAIN = 10;

export default {
  name: "EdgeVerdict",
  props: {
    game: { type: Object, required: true },
    // The evidence line under the win % (value, key number crossed, books'
    // lean). Off on the Home card, where it was the one line nobody read;
    // the game page turns it on.
    detail: { type: Boolean, default: false },
    // "card" is the Home card: the verdict sits on the card itself rather than
    // in a tinted box, led by the edge team's logo, with the frozen / market
    // line under it. The game page keeps the default box.
    layout: { type: String, default: "box" },
  },
  data() {
    return { activeKeyNumber: null };
  },
  computed: {
    edge() {
      return computeEdge(this.game);
    },
    // The one line under the win % — see `verdictLine`.
    lineItems() {
      return verdictLine(this.game, this.edge);
    },
    valueClass() {
      return this.edge.lineValueProbability >= 0.05
        ? "font-semibold text-slate-300"
        : "font-semibold text-slate-500";
    },
    homeAbbr() {
      return this.game.homeTeam.abbr;
    },
    awayAbbr() {
      return this.game.awayTeam.abbr;
    },

    // ---- Tug bar -------------------------------------------------------
    // Everything below is the AWAY team's share of the bar, so the home team
    // fills from the right. `home_spread` is negative when home is favoured,
    // which shrinks the away share -- the favourite visibly owns more of it.
    awayAccent() {
      return teamAccent(this.game.awayTeam.name || this.game.awayTeam.abbr);
    },
    homeAccent() {
      return teamAccent(this.game.homeTeam.name || this.game.homeTeam.abbr);
    },
    // Flat tints, so the bright band reads as the one thing that moved.
    awayFill() {
      return `${this.awayAccent}73`;
    },
    homeFill() {
      return `${this.homeAccent}73`;
    },
    // The two colours meet where the books sit now; the band then shows the
    // edge side's colour having grown from the frozen mark to that point.
    splitShare() {
      return this.edge.market != null ? this.marketShare : this.frozenShare;
    },
    tugCentre() {
      const { frozen, market } = this.edge;
      if (frozen == null) return 0;
      return market == null ? frozen : (frozen + market) / 2;
    },
    keyNumberMarks() {
      const lo = this.tugCentre - TUG_HALF_WINDOW_POINTS;
      const hi = this.tugCentre + TUG_HALF_WINDOW_POINTS;
      return [0, ...TUG_KEY_NUMBERS.flatMap((n) => [-n, n])]
        .filter((spread) => spread > lo && spread < hi)
        .map((spread) => ({
          spread,
          share: this.shareFor(spread),
          label: Math.abs(spread),
          tip: this.keyNumberTip(spread),
        }));
    },
    frozenShare() {
      return this.shareFor(this.edge.frozen);
    },
    marketShare() {
      return this.shareFor(this.edge.market);
    },
    // Team-first, so each caption stands on its own -- and so a favourite-flip
    // shows up as the abbreviation changing.
    frozenLine() {
      return formatLine(this.edge.frozen, this.homeAbbr, this.awayAbbr);
    },
    marketLine() {
      return formatLine(this.edge.market, this.homeAbbr, this.awayAbbr);
    },
    // Is the market's tick to the LEFT of the frozen tick? Named for the tick,
    // deliberately: this is NOT "the market moved toward the team on the left".
    // The tick is a boundary in a tug of war, so it slides into the loser's
    // territory -- a home team getting more favoured pulls it left, and home is
    // drawn on the right.
    marketIsLeft() {
      return this.edge.market != null && this.marketShare < this.frozenShare;
    },
    // The two captions in the order their ticks sit on the bar. Each carries
    // its own label and dot colour, because which one comes first is not fixed.
    frozenCaption() {
      return {
        label: "Frozen",
        value: this.frozenLine,
        dotClass: "bg-white",
        labelClass: "text-slate-500",
        valueClass: "text-slate-100",
      };
    },
    marketCaption() {
      const present = this.edge.market != null;
      if (!present) {
        return {
          label: "Market",
          value: this.marketLine,
          dotClass: "bg-slate-600",
          labelClass: "text-slate-600",
          valueClass: "text-slate-600",
        };
      }
      const c = this.edgeSideAccent;
      return {
        label: "Market",
        value: this.marketLine,
        dotClass: "",
        labelClass: "",
        valueClass: "",
        dotStyle: { background: c, filter: "brightness(1.4)" },
        labelStyle: { color: c, filter: "brightness(1.4)" },
        valueStyle: { color: c, filter: "brightness(1.6)" },
      };
    },
    leftCaption() {
      return this.marketIsLeft ? this.marketCaption : this.frozenCaption;
    },
    rightCaption() {
      return this.marketIsLeft ? this.frozenCaption : this.marketCaption;
    },
    showBand() {
      return (
        this.edge.measurable && this.edge.points > 0 && this.edge.market != null
      );
    },
    bandLeft() {
      return Math.min(this.frozenShare, this.marketShare);
    },
    bandWidth() {
      return Math.abs(this.marketShare - this.frozenShare);
    },

    // ---- Verdict -------------------------------------------------------
    edgeSideAbbr() {
      if (!this.edge.side) return "";
      return this.edge.side === "home" ? this.homeAbbr : this.awayAbbr;
    },
    card() {
      return this.layout === "card";
    },
    // Where the Home card's two numbers hang. With no market there is only the
    // frozen number, centred on its mark. Each one stands MARK_LABEL_GAP clear
    // of its own mark, away from the other: marks a tenth of a point apart sit
    // on the same pixel, and with no gap of their own the two numbers ran
    // together ("LAR -3.3FROZEN LAR -3.5", "HOU -10.5MARKET").
    leftMarkLabelStyle() {
      if (this.edge.market == null) {
        return { left: `${this.frozenShare}%`, transform: "translateX(-50%)" };
      }
      return { right: `${100 - Math.min(this.frozenShare, this.marketShare)}%`, marginRight: `${MARK_LABEL_GAP}px` };
    },
    rightMarkLabelStyle() {
      return { left: `${Math.max(this.frozenShare, this.marketShare)}%`, marginLeft: `${MARK_LABEL_GAP}px` };
    },
    edgeSideLogo() {
      if (!this.edge.side) return "";
      const team = this.edge.side === "home" ? this.game.homeTeam : this.game.awayTeam;
      return team.logo || teamLogo(team.abbr);
    },
    // The Home card's verdict row wears the edge team's colour, so the colour
    // says whose side it is and the number's own colour says how strong.
    // Without an edge side (no market yet) it is a plain quiet surface.
    cardVerdictStyle() {
      if (!this.edge.side || !this.edge.measurable) {
        return { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" };
      }
      const c = this.edgeSideAccent;
      return { background: `linear-gradient(90deg, ${c}57, ${c}0f)`, border: `1px solid ${c}80` };
    },
    edgeSideAccent() {
      if (!this.edge.side) return "#94a3b8";
      const team =
        this.edge.side === "home" ? this.game.homeTeam : this.game.awayTeam;
      return teamAccent(team.name || team.abbr);
    },
    // Banded on what the gap buys, not how wide it is. The ladder and its
    // words live in `edge.js` so the card and /history cannot drift apart.
    edgeBand() {
      return bandFor(this.edge.lineValueProbability);
    },
    strengthLabel() {
      return bandLabel(this.edge.lineValueProbability);
    },
    edgeTextClass() {
      return BAND_TEXT_CLASS[this.edgeBand];
    },
    edgeGlow() {
      return BAND_GLOW[this.edgeBand] || null;
    },
    meterFillClass() {
      return {
        strong: "bg-strength-lime",
        solid: "bg-strength-lime",
        slight: "bg-strength-slight",
        flat: "bg-slate-400",
      }[this.edgeBand];
    },
    // A genuinely zero edge lights nothing: the floor of 1 said "some" beside
    // a number reading 50.0%. Anything above zero still rounds up to a segment.
    meterFilled() {
      const gained = this.edge.lineValueProbability || 0;
      if (gained <= 0) return 0;
      return Math.max(1, Math.min(10, Math.round((gained / METER_MAX_GAIN) * 10)));
    },
    meterLabel() {
      return `${this.strengthLabel} edge, ${this.meterFilled} of 10`;
    },
    // Tinted by the same band, so the verdict block itself reads as the
    // headline without needing a full-width coloured stripe across the card.
    verdictSurfaceClass() {
      if (!this.edge.measurable) {
        return "bg-white/[0.03] ring-white/[0.08]";
      }
      return {
        strong: "bg-emerald-500/[0.12] ring-emerald-400/25",
        solid: "bg-emerald-500/[0.08] ring-emerald-400/15",
        slight: "bg-amber-500/[0.07] ring-amber-400/15",
        flat: "bg-white/[0.04] ring-white/10",
      }[this.edgeBand];
    },
    coverLabel() {
      return winLabel(this.edge) ?? "—";
    },
    // Sits under a "Frozen" heading, so it has to read as a due date rather
    // than as the value itself: "due Wed", not "Wednesday".
    dueLabel() {
      const kind = this.game.lineEmailExpected;
      if (!kind) return "due";
      return `due ${kind.charAt(0).toUpperCase()}${kind.slice(1, 3)}`;
    },
  },
  methods: {
    // What a mark is worth, in this game: how often it ends on exactly that
    // margin under the market's own distribution (`marginPmf`, the same model
    // the edge is scored on), and whether our number wins it and the market's
    // does not. A mark at home spread x is the game where home wins by -x.
    keyNumberTip(spread) {
      if (spread === 0) return "Pick'em. Across 0 the favourite changes sides.";
      const n = Math.abs(spread);
      const winner = spread < 0 ? this.homeAbbr : this.awayAbbr;
      const line = this.edge.market ?? this.edge.frozen;
      const chance = marginPmf(line).get(-spread) ?? 0;
      const pct = (chance * 100).toFixed(1);
      const { frozen, market } = this.edge;
      const inGap =
        market != null &&
        spread > Math.min(frozen, market) &&
        spread < Math.max(frozen, market);
      const lead = `${winner} wins by exactly ${n} in ${pct}% of games lined like this.`;
      return inGap
        ? `${lead} Inside the gap: your number wins that game and the market's doesn't.`
        : lead;
    },
    shareFor(homeSpread) {
      if (homeSpread == null || Number.isNaN(homeSpread)) return 50;
      const share = 50 + (homeSpread - this.tugCentre) * TUG_PERCENT_PER_POINT;
      return Math.max(0, Math.min(100, share));
    },
  },
};
</script>
